import { useEffect, useRef, useCallback } from 'react';

export interface KeyboardShortcut {
  key: string;
  ctrlOrCmd?: boolean;
  shift?: boolean;
  alt?: boolean;
  callback: () => void | Promise<void>;
  isEnable: boolean;
  allowInInput?: boolean;
}

const activeShortcuts = new Set<string>();

export const useKeyboardShortcuts = (shortcuts: KeyboardShortcut[]) => {
  const shortcutsRef = useRef<KeyboardShortcut[]>([]);

  useEffect(() => {
    shortcutsRef.current = shortcuts;
  }, [shortcuts]);

  const handleKeyDown = useCallback(async (event: KeyboardEvent) => {
    const isCtrlOrMetaKeyPressed = event.ctrlKey || event.metaKey;
    const target = event.target as HTMLElement;
    // Read-only CRM generated-row cells aren't contentEditable, so they need an explicit
    // marker here to fall back to native copy instead of the app's block-JSON copy.
    const isInsideGeneratedRowCell = !!target.closest?.('.py-generated-row-cell');
    const isInputElement =
      target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable || isInsideGeneratedRowCell;

    for (const shortcut of shortcutsRef.current) {
      const keyMatches = event.key.toLowerCase() === shortcut.key.toLowerCase();
      const ctrlMatches = shortcut.ctrlOrCmd ? isCtrlOrMetaKeyPressed : !isCtrlOrMetaKeyPressed;
      const shiftMatches = shortcut.shift ? event.shiftKey : !event.shiftKey;

      // Skip input elements unless the shortcut explicitly allows it
      if (isInputElement && !shortcut.allowInInput) {
        continue;
      }

      if (keyMatches && ctrlMatches && shiftMatches && shortcut.isEnable) {
        event.preventDefault();
        event.stopPropagation();
        await shortcut.callback();
        return;
      }
    }
  }, []);

  useEffect(() => {
    const shortcutId = shortcuts.map((s) => `${s.key}-${s.ctrlOrCmd}-${s.shift}-${s.alt}`).join('|');

    if (!activeShortcuts.has(shortcutId)) {
      activeShortcuts.add(shortcutId);
      document.addEventListener('keydown', handleKeyDown);

      return () => {
        document.removeEventListener('keydown', handleKeyDown);
        activeShortcuts.delete(shortcutId);
      };
    }
  }, [shortcuts, handleKeyDown]);
};
